// UserProfileModal.tsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
    User,
    Shield,
    Laptop,
    Smartphone,
    MoreHorizontal,
    Plus,
    Loader2,
    X
} from 'lucide-react';
import { useSession, authClient, API_URL } from '@/config/auth';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { widgetService } from "@/api/services/widgetService";
import { HttpClient } from "@/api/services/HttpClient";

interface UserProfileModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type Tab = 'profile' | 'security';

export const UserProfileModal: React.FC<UserProfileModalProps> = ({
    open,
    onOpenChange
}) => {
    const [activeTab, setActiveTab] = useState<Tab>('profile');
    const { data: session } = useSession();

    if (!session) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[850px] p-0 overflow-hidden h-[600px] flex gap-0 bg-background border-none shadow-xl sm:rounded-2xl">
                <DialogTitle className="sr-only">User Profile</DialogTitle>

                {/* Sidebar */}
                <div className="w-[260px] bg-muted/30 border-r flex flex-col justify-between p-6">
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-semibold tracking-tight text-foreground">Account</h2>
                            <p className="text-xs text-muted-foreground mt-1">Manage your account info.</p>
                        </div>

                        <nav className="flex flex-col gap-1">
                            <NavButton
                                active={activeTab === 'profile'}
                                onClick={() => setActiveTab('profile')}
                                icon={<User className="w-4 h-4" />}
                                label="Profile"
                            />
                            <NavButton
                                active={activeTab === 'security'}
                                onClick={() => setActiveTab('security')}
                                icon={<Shield className="w-4 h-4" />}
                                label="Security"
                            />
                        </nav>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-white dark:bg-zinc-950">
                    {activeTab === 'profile' ? (
                        <ProfileTab user={session.user} />
                    ) : (
                        <SecurityTab currentSessionId={session.session?.id} />
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

// --- Sub-Components ---

const NavButton = ({ active, onClick, icon, label }: any) => (
    <Button
        variant="ghost"
        className={cn(
            "justify-start gap-3 h-10 font-medium px-4 transition-all duration-200",
            active
                ? "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        )}
        onClick={onClick}
    >
        {icon}
        {label}
    </Button>
);

const ProfileTab = ({ user }: { user: any }) => {
    const { toast } = useToast();

    // editor state
    const [editOpen, setEditOpen] = useState(false);
    const [nameInput, setNameInput] = useState(user?.name ?? '');
    const [avatarPreview, setAvatarPreview] = useState<string | null>(user?.image ?? null);
    const [avatarFile, setAvatarFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);

    useEffect(() => {
        if (!editOpen) {
            setNameInput(user?.name ?? '');
            setAvatarPreview(user?.image ?? null);
            setAvatarFile(null);
        }
    }, [user]);

    const handlePick = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast({ title: "File too large", description: "Avatar must be <= 2MB", variant: "destructive" });
            return;
        }
        if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
            toast({ title: "Invalid file type", description: "Use PNG, JPEG or SVG", variant: "destructive" });
            return;
        }

        setAvatarFile(file);
        setAvatarPreview(URL.createObjectURL(file));
    };

    const uploadAvatarAndGetUrl = async (file: File) => {
        setUploading(true);
        setUploadProgress(0);

        try {
            // widgetService.uploadWidgetAsset(file, token) => { fileUrl: string }
            const res = await widgetService.uploadWidgetAsset(file);

            // some uploaders return { fileUrl } or { data: { fileUrl } }, handle both
            const fileUrl = res;
            if (!fileUrl) throw new Error("No file URL returned from uploader");

            setUploadProgress(100);
            return fileUrl;
        } finally {
            setUploading(false);
            setTimeout(() => setUploadProgress(null), 400); // brief UI cleanup
        }
    };

    const handleSave = async () => {
        if (!nameInput?.trim()) {
            toast({ title: "Name required", variant: "destructive" });
            return;
        }

        setSaving(true);
        try {
            let avatarUrl = user.image ?? null;

            if (avatarFile) {
                avatarUrl = await uploadAvatarAndGetUrl(avatarFile);
            }

            if (!authClient || typeof (authClient as any).updateUser !== 'function') {
                throw new Error('authClient.updateUser is not available');
            }

            // call exactly as you specified
            const resp = await (authClient as any).updateUser({
                name: nameInput.trim(),
                image: avatarUrl,
            });

            // Optionally handle resp / error. Many SDKs return { data, error } or similar.
            // Keep UI in sync locally:
            setAvatarFile(null);
            setAvatarPreview(avatarUrl ?? null);
            setEditOpen(false);

            toast({ title: "Profile updated" });
            // If you want to force-refresh the session from server, you can reload or
            // call any SDK session-refresh method you have. I avoid reload here and just update UI.
        } catch (err) {
            console.error('Failed to update user', err);
            toast({ title: "Failed to update profile", variant: "destructive" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-8 max-w-2xl">
            <h3 className="text-lg font-semibold mb-6">Profile details</h3>
            <Separator className="mb-8" />

            {/* Profile Row */}
            <div className="flex items-center justify-between py-2 mb-8">
                <span className="text-sm font-medium w-1/3">Profile</span>
                <div className="flex-1 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 border">
                            <AvatarImage src={avatarPreview ?? undefined} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                                {user.name?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium">{user.name}</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/80 font-medium"
                        onClick={() => setEditOpen(true)}
                    >
                        Update profile
                    </Button>
                </div>
            </div>

            <Separator className="my-6" />

            {/* Email Row */}
            <div className="flex items-start justify-between py-2">
                <span className="text-sm font-medium w-1/3 pt-2">Email addresses</span>
                <div className="flex-1 space-y-4">
                    <div className="flex items-center justify-between group">
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground">{user.email}</span>
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-5 font-normal bg-muted text-muted-foreground">
                                Primary
                            </Badge>
                        </div>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </Button>
                    </div>

                    {/* <Button variant="ghost" className="h-auto p-0 text-muted-foreground hover:text-foreground text-sm font-normal gap-2 pl-0 hover:bg-transparent">
                        <Plus className="w-4 h-4" />
                        Add email address
                    </Button> */}
                </div>
            </div>

            {/* Edit modal */}
            {editOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setEditOpen(false)} />
                    <div className="bg-background p-6 rounded-lg z-10 w-[520px]">
                        <h4 className="font-medium mb-3">Update profile</h4>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-muted-foreground">Name</label>
                                <input
                                    value={nameInput}
                                    onChange={(e) => setNameInput(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Your name"
                                />
                            </div>

                            <div>
                                <label className="text-xs text-muted-foreground">Avatar</label>
                                <div className="flex items-center gap-3 mt-2">
                                    <div className="h-12 w-12 rounded-lg border flex items-center justify-center overflow-hidden">
                                        {avatarPreview ? (
                                            // eslint-disable-next-line @next/next/no-img-element
                                            <img src={avatarPreview} alt="preview" className="h-full w-full object-cover" />
                                        ) : (
                                            <div className="text-xs text-muted-foreground">No avatar</div>
                                        )}
                                    </div>

                                    <div className="flex gap-2 items-center">
                                        {/* programmatic trigger to avoid label issues in modals/portals */}
                                        <div onClick={() => document.getElementById('user-avatar-input')?.click()}>
                                            <Button size="sm" variant="outline">Choose file</Button>
                                        </div>

                                        <Button size="sm" variant="ghost" onClick={() => { setAvatarFile(null); setAvatarPreview(user.image ?? null); }}>
                                            Reset
                                        </Button>

                                        <input
                                            id="user-avatar-input"
                                            type="file"
                                            accept="image/png,image/jpeg,image/svg+xml"
                                            className="hidden"
                                            onChange={handlePick}
                                        />
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">PNG, JPEG or SVG. Max 2MB.</p>
                            </div>

                            {uploadProgress !== null && (
                                <div>
                                    <div className="w-full bg-muted/30 h-2 rounded overflow-hidden">
                                        <div style={{ width: `${uploadProgress}%` }} className="h-2 bg-primary" />
                                    </div>
                                    <p className="text-xs mt-1">{uploadProgress}%</p>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={saving || uploading}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSave} disabled={saving || uploading}>
                                    {saving ? "Saving..." : "Save changes"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SecurityTab = ({ currentSessionId }: { currentSessionId: string }) => {
    const [sessions, setSessions] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const { toast } = useToast();

    useEffect(() => {
        let mounted = true;

        const normalizeSessions = (raw: any[]): any[] => {
            if (!Array.isArray(raw)) return [];

            return raw.map(s => {
                const id = s.id ?? s.sessionId ?? s.token ?? s.sid ?? null;
                const token = s.token ?? s.id ?? s.sessionToken ?? null;
                const userAgent = s.userAgent ?? s.ua ?? s.client ?? '';
                const ipAddress = s.ipAddress ?? s.ip ?? s.remoteAddr ?? '';
                const updatedAt = s.updatedAt ?? s.lastActiveAt ?? s.lastSeen ?? s.updated_at ?? null;
                const ipLocation = s.ipLocation ?? s.location ?? s.city ?? null;

                return {
                    id,
                    token,
                    userAgent,
                    ipAddress,
                    updatedAt,
                    ipLocation,
                    __raw: s,
                };
            });
        };

        const loadSessions = async () => {
            setIsLoading(true);
            try {
                let res: any = null;

                if (typeof (authClient as any)?.listSessions === 'function') {
                    res = await (authClient as any).listSessions();
                }

                let rawSessions: any[] = [];
                if (res) {
                    if (Array.isArray(res)) rawSessions = res;
                    else if (Array.isArray(res.data)) rawSessions = res.data;
                    else if (Array.isArray(res.sessions)) rawSessions = res.sessions;
                    else if (Array.isArray(res.result)) rawSessions = res.result;
                    else if (Array.isArray(res.items)) rawSessions = res.items;
                    else rawSessions = [res];
                }

                if (!res || rawSessions.length === 0) {
                    try {
                        const authToken = await HttpClient.getAuthToken();
                        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                        if (authToken) {
                            headers['Authorization'] = `Bearer ${authToken}`;
                        }
                        const r = await fetch(`${API_URL}/auth/session/list`, {
                            method: 'GET',
                            headers,
                        });

                        if (r.ok) {
                            const payload = await r.json();
                            if (Array.isArray(payload)) rawSessions = payload;
                            else if (Array.isArray(payload.data)) rawSessions = payload.data;
                            else if (Array.isArray(payload.sessions)) rawSessions = payload.sessions;
                            else if (Array.isArray(payload.items)) rawSessions = payload.items;
                            else if (payload?.data && Array.isArray(payload.data?.sessions)) rawSessions = payload.data.sessions;
                        } else {
                            console.warn('Failed to fetch sessions via fallback:', r.status, await r.text().catch(() => ''));
                        }
                    } catch (err) {
                        console.warn('Fallback session fetch failed', err);
                    }
                }

                const normalized = normalizeSessions(rawSessions);
                if (mounted) setSessions(normalized);
            } catch (error) {
                console.error("Failed to fetch sessions", error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        };

        loadSessions();

        return () => {
            mounted = false;
        };
    }, []);

    const handleRevoke = async (token: string) => {
        try {
            if (authClient && typeof (authClient as any).revokeSession === 'function') {
                await (authClient as any).revokeSession({ token });
            }

            setSessions(prev => prev.filter(s => s.token !== token && s.id !== token));
            toast({ title: "Device revoked successfully" });
        } catch (e) {
            console.error('Failed to revoke session', e);
            toast({ title: "Failed to revoke device", variant: "destructive" });
        }
    };

    // ---------------- Password change logic ----------------
    const resetPasswordForm = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordError(null);
    };

    const validateNewPassword = (pwd: string) => {
        // Basic checks: length >= 8 and includes letter+number. Adjust if you have stronger rules.
        if (pwd.length < 8) return "Password must be at least 8 characters";
        if (!/[A-Za-z]/.test(pwd) || !/[0-9]/.test(pwd)) return "Password must include letters and numbers";
        return null;
    };

    const handleChangePassword = async () => {
        setPasswordError(null);

        if (!newPassword || !confirmPassword) {
            setPasswordError("Please fill all fields");
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError("Passwords do not match");
            return;
        }
        const strengthErr = validateNewPassword(newPassword);
        if (strengthErr) {
            setPasswordError(strengthErr);
            return;
        }

        setPasswordSaving(true);
        try {
            // try SDK helpers in common locations
            if (authClient && typeof (authClient as any).changePassword === 'function') {
                await (authClient as any).changePassword({ currentPassword, newPassword, revokeOtherSessions: true });
            }

            toast({ title: "Password updated" });
            setPasswordModalOpen(false);
            resetPasswordForm();
        } catch (err: any) {
            console.error("Password change failed", err);
            const msg = (err?.message) ? err.message : "Failed to update password";
            setPasswordError(msg);
            toast({ title: "Failed to update password", description: msg, variant: "destructive" });
        } finally {
            setPasswordSaving(false);
        }
    };

    return (
        <div className="p-8 max-w-2xl">
            <h3 className="text-lg font-semibold mb-6">Security</h3>
            <Separator className="mb-8" />

            {/* Password Row */}
            <div className="flex items-center justify-between py-2 mb-8">
                <span className="text-sm font-medium w-1/3">Password</span>
                <div className="flex-1 flex items-center justify-between">
                    <div className="flex items-center gap-1">
                        <div className="flex gap-0.5">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="w-1.5 h-1.5 rounded-full bg-foreground" />
                            ))}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-primary hover:text-primary/80 font-medium"
                        // TODO: Implement password update from better auth docs
                        onClick={() => {
                            resetPasswordForm();
                            setPasswordModalOpen(true);
                        }}
                    >
                        Update password
                    </Button>
                </div>
            </div>

            <Separator className="my-6" />

            {/* Active Devices Row */}
            <div className="flex items-start justify-between py-2">
                <span className="text-sm font-medium w-1/3 pt-1">Active devices</span>

                <div className="flex-1 space-y-6">
                    {isLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading devices...
                        </div>
                    ) : (
                        sessions.map((session) => {
                            const isCurrent = session.id === currentSessionId || session.token === currentSessionId;
                            const ua = (session.userAgent || '').toLowerCase();
                            const isMobile = ua.includes("mobile") || ua.includes("iphone") || ua.includes("android");

                            // friendly device name fallback
                            const deviceName = isMobile ? 'Mobile device' : 'Desktop device';

                            return (
                                <div key={session.id ?? session.token ?? Math.random()} className="flex items-start gap-4 group">
                                    <div className="mt-1">
                                        {isMobile ? <Smartphone className="w-8 h-8 text-foreground" /> : <Laptop className="w-8 h-8 text-foreground" />}
                                    </div>

                                    <div className="flex-1 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium">
                                                {deviceName}
                                            </span>
                                            {isCurrent && (
                                                <Badge variant="secondary" className="text-[10px] px-1.5 h-5 font-normal bg-muted text-muted-foreground">
                                                    This device
                                                </Badge>
                                            )}
                                        </div>

                                        <div className="text-xs text-muted-foreground space-y-0.5">
                                            <p>{(session.userAgent || '').substring(0, 30)}{(session.userAgent || '').length > 30 ? '...' : ''}</p>
                                            <p>{session.ipLocation || 'Unknown Location'} • {session.ipAddress || ''}</p>
                                            <p className={isCurrent ? "text-emerald-600 font-medium" : ""}>
                                                {isCurrent ? "Active now" : (session.updatedAt ? `Last active ${new Date(session.updatedAt).toLocaleDateString()}` : '')}
                                            </p>
                                        </div>
                                    </div>

                                    {!isCurrent && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={() => handleRevoke(session.token ?? session.id)}
                                        >
                                            {/*Use a small cross icon to revoke the session */}
                                            {/* <MoreHorizontal className="w-4 h-4 text-muted-foreground" /> */}
                                            <X className="w-4 h-4 text-muted-foreground" />
                                        </Button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Password modal */}
            {passwordModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setPasswordModalOpen(false)} />
                    <div className="bg-background p-6 rounded-lg z-10 w-[520px]">
                        <h4 className="font-medium mb-3">Change password</h4>

                        <div className="space-y-4">
                            <div>
                                <label className="text-xs text-muted-foreground">Current password</label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Current password"
                                />
                            </div>

                            <div>
                                <label className="text-xs text-muted-foreground">New password</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="New password"
                                />
                            </div>

                            <div>
                                <label className="text-xs text-muted-foreground">Confirm new password</label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="w-full border rounded px-3 py-2"
                                    placeholder="Confirm new password"
                                />
                            </div>

                            {passwordError && (
                                <div className="text-sm text-red-600">{passwordError}</div>
                            )}

                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="ghost" onClick={() => { setPasswordModalOpen(false); resetPasswordForm(); }} disabled={passwordSaving}>
                                    Cancel
                                </Button>
                                <Button onClick={handleChangePassword} disabled={passwordSaving}>
                                    {passwordSaving ? "Saving..." : "Save password"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
